import { useEffect, useRef, useState } from 'react';
import { jsonOrThrow } from '../apiJson';
import { HeaderMoreMenu } from './HeaderMoreMenu';
import { LogoMark } from './LogoMark';
import { SiteFooter } from './SiteFooter';
import { PageHead } from './PageHead';
import { FeaturedPremiumShopSection } from './FeaturedPremiumShopSection';
import { ProductCard } from './ProductCard';
import { CategoryMenuDropdown } from './CategoryMenuDropdown';
import './SearchResultsPage.css';
import './LandingPage.css';
import './AboutPage.css';
import './PremiumPage.css';

const PLACEHOLDER_PRODUCT_IMAGE = `${process.env.PUBLIC_URL}/placeholders/product-image.svg`;
const PLACEHOLDER_SHOP_LOGO = `${process.env.PUBLIC_URL}/placeholders/shop-logo.svg`;

const DEMO_PREMIUM_SHOP = {
  rootDomain: 'example-shop.mt',
  name: 'Your Shop',
  faviconUrl: PLACEHOLDER_SHOP_LOGO,
  shippingMessage: '2–3 business days',
  tagline: 'Your products, front and center for shoppers already searching to buy',
};

const DEMO_PRODUCT_CARD = {
  id: 'premium-card-demo',
  title: 'Example product',
  url: 'https://example-shop.mt/products/example',
  image: PLACEHOLDER_PRODUCT_IMAGE,
  currency: 'EUR',
  priceNow: 49.99,
  priceWas: 69.99,
  merchant: 'Your Shop',
  inStock: true,
  description:
    'Example product description for the Premium card preview. Premium listings show up to three lines so shoppers get more detail before they click through to your store.',
};

const DEMO_FEATURED_PRODUCTS = [
  {
    id: 'premium-demo-1',
    title: 'Example product',
    url: 'https://example-shop.mt/products/1',
    image: PLACEHOLDER_PRODUCT_IMAGE,
    currency: 'EUR',
    priceNow: 79.99,
    priceWas: 99.99,
    merchant: 'Your Shop',
    inStock: true,
  },
  {
    id: 'premium-demo-2',
    title: 'Example product',
    url: 'https://example-shop.mt/products/2',
    image: PLACEHOLDER_PRODUCT_IMAGE,
    currency: 'EUR',
    priceNow: 34.5,
    priceWas: null,
    merchant: 'Your Shop',
    inStock: true,
  },
  {
    id: 'premium-demo-3',
    title: 'Example product',
    url: 'https://example-shop.mt/products/3',
    image: PLACEHOLDER_PRODUCT_IMAGE,
    currency: 'EUR',
    priceNow: 42,
    priceWas: 55,
    merchant: 'Your Shop',
    inStock: true,
  },
  {
    id: 'premium-demo-4',
    title: 'Example product',
    url: 'https://example-shop.mt/products/4',
    image: PLACEHOLDER_PRODUCT_IMAGE,
    currency: 'EUR',
    priceNow: 29.99,
    priceWas: null,
    merchant: 'Your Shop',
    inStock: true,
  },
];

const PREMIUM_CARD_FEATURES = [
  {
    id: 'image',
    title: 'Larger product image',
    copy: 'Product image is 5% larger than on standard cards.',
  },
  {
    id: 'shipping',
    title: 'Shipping cue',
    copy: 'Shipping message is displayed and can be updated by the shop owner.',
  },
  {
    id: 'freshness',
    title: 'Stock freshness signal',
    copy: '“✓ today” shows stock status was checked today.',
  },
  {
    id: 'description',
    title: 'Longer description',
    copy: 'Description shows up to 3 lines instead of 2.',
  },
  {
    id: 'cta',
    title: 'Branded product CTA',
    copy: 'Shows your shop logo and “Visit Your Shop” instead of a generic “Visit store”.',
  },
];

function PremiumCardFeatureGuide() {
  const cardRef = useRef(null);
  const [activeFeature, setActiveFeature] = useState(null);

  useEffect(() => {
    const root = cardRef.current;
    if (!root) {
      return undefined;
    }

    const nodes = root.querySelectorAll('[data-premium-feature]');

    function onEnter(event) {
      const id = event.currentTarget.getAttribute('data-premium-feature');
      setActiveFeature(id);
    }

    function onLeave(event) {
      const related = event.relatedTarget;
      if (
        related &&
        typeof related.closest === 'function' &&
        root.contains(related)
      ) {
        const next = related.closest('[data-premium-feature]');
        if (next) {
          setActiveFeature(next.getAttribute('data-premium-feature'));
          return;
        }
      }
      setActiveFeature(null);
    }

    nodes.forEach((node) => {
      node.addEventListener('pointerenter', onEnter);
      node.addEventListener('pointerleave', onLeave);
    });

    return () => {
      nodes.forEach((node) => {
        node.removeEventListener('pointerenter', onEnter);
        node.removeEventListener('pointerleave', onLeave);
      });
    };
  }, []);

  return (
    <div
      className="premium-page__card-guide"
      data-active-feature={activeFeature || undefined}
    >
      <div
        ref={cardRef}
        className="premium-page__card-demo"
        aria-label="Premium product card preview"
        onClickCapture={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <ul className="results-page__grid premium-page__card-demo-grid">
          <li className="results-page__cell">
            <ProductCard
              product={DEMO_PRODUCT_CARD}
              layout="grid"
              premiumShop={DEMO_PREMIUM_SHOP}
              outboundCampaign="premium_page_demo"
              annotatePremiumFeatures
            />
          </li>
        </ul>
      </div>
      <ol className="premium-page__card-feature-list" aria-label="Premium card differences">
        {PREMIUM_CARD_FEATURES.map((feature, index) => (
          <li
            key={feature.id}
            className={
              activeFeature === feature.id
                ? 'premium-page__card-feature is-active'
                : 'premium-page__card-feature'
            }
            onPointerEnter={() => setActiveFeature(feature.id)}
            onPointerLeave={() => setActiveFeature(null)}
          >
            <span className="premium-page__card-feature-index" aria-hidden="true">
              {index + 1}
            </span>
            <div>
              <strong>{feature.title}</strong>
              <span>{feature.copy}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function BenefitIcon({ children }) {
  return (
    <span className="premium-page__benefit-icon" aria-hidden="true">
      {children}
    </span>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M2.5 12s3.5-6.5 9.5-6.5S21.5 12 21.5 12s-3.5 6.5-9.5 6.5S2.5 12 2.5 12Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.75" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M3.5 5h1.7l1.4 10.2a1.5 1.5 0 0 0 1.5 1.3h8.7a1.5 1.5 0 0 0 1.5-1.25L19.5 8H7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="9.5" cy="19.5" r="1.25" fill="currentColor" />
      <circle cx="16.5" cy="19.5" r="1.25" fill="currentColor" />
    </svg>
  );
}

function GrowthIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 17.5 10 11l3.5 3.5L20 7.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.5 7.5H20v5.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M4.5 12a7.5 7.5 0 0 1 12.7-5.4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M17.2 3.8v3.8h-3.8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.5 12a7.5 7.5 0 0 1-12.7 5.4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M6.8 20.2v-3.8h3.8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 7h11M4 12h8M4 17h5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="m15 14 3 3 3-3M18 7v10"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RankIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 19V9.5M12 19V5M19 19v-6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M4 19h16"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3.5 5 6.5v5.8c0 4.2 2.9 7.9 7 9.2 4.1-1.3 7-5 7-9.2V6.5L12 3.5Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="m9.2 12.2 1.8 1.8 3.8-3.8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PremiumPage() {
  const [browseCategories, setBrowseCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;

    fetch('/api/browse-websites.php')
      .then((res) => jsonOrThrow(res))
      .then((data) => {
        if (!cancelled) {
          setBrowseCategories(Array.isArray(data?.categories) ? data.categories : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBrowseCategories([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="results-page">
      <PageHead
        title="Premium package | Shops.mt"
        description="List your Maltese shop with Premium on Shops.mt - boosted search placement, branded product cards, 24-hour updates, and homepage features."
        canonicalPath="/premium"
        robots="noindex, follow"
      />
      <header className="results-page__header premium-page__header">
        <div className="results-page__header-inner premium-page__header-inner">
          <div className="results-page__header-left">
            <LogoMark variant="header" />
            <HeaderMoreMenu />
          </div>
        </div>
      </header>

      <div className="about-page premium-page">
        <header className="premium-page__masthead">
          <p className="premium-page__eyebrow">
            Shops.mt: Malta&apos;s Shopping Search Engine
          </p>
          <h1 id="premium-heading" className="about-page__title premium-page__title">
            Premium Package: Get Featured, Boosted, and Branded on Shops.mt
          </h1>
          <div className="premium-page__main-text">
            <p>
              Shops.mt is Malta&apos;s first dedicated shopping search engine,
              built to be the starting point for local shoppers looking for
              products online. Since inception in June 2026:
            </p>
            <ul className="premium-page__stats">
              <li>
                Roughly <strong className="premium-page__stat">15,000</strong>{' '}
                page views a month
              </li>
              <li>
                <strong className="premium-page__stat">19 seconds</strong> spent
                on the homepage
              </li>
              <li>
                <strong className="premium-page__stat">1 minute 20 seconds</strong>{' '}
                per search results page
              </li>
              <li>
                <strong className="premium-page__stat">26.5% CTR</strong> on
                search and browse - shoppers are motivated and actively comparing
              </li>
            </ul>
            <p>
              New shops joining Shops.mt are onboarded exclusively through the
              Premium Package, giving your shop enhanced branding via premium
              product cards, boosted placement, listings refreshed within 24 hours,
              and eligibility to get featured on the homepage and category pages.
            </p>
          </div>
        </header>

        <div className="premium-page__hero">
          <div className="premium-page__intro">
            <h2 className="premium-page__benefits-title">
              What you get with Premium
            </h2>
            <ul className="premium-page__benefits" aria-label="Premium benefits">
              <li>
                <BenefitIcon>
                  <GrowthIcon />
                </BenefitIcon>
                <div>
                  <strong>Featured eligibility</strong>
                  <span>
                    Get in front of shoppers on the homepage and category pages.
                  </span>
                </div>
              </li>
              <li>
                <BenefitIcon>
                  <CartIcon />
                </BenefitIcon>
                <div>
                  <strong>Branded product cards</strong>
                  <span>
                    Stand out with your logo, fuller imagery, and clearer trust
                    signals.
                  </span>
                </div>
              </li>
              <li>
                <BenefitIcon>
                  <EyeIcon />
                </BenefitIcon>
                <div>
                  <strong>Boosted placement</strong>
                  <span>
                    When your products show in search results, Premium boosts
                    them closer to the top under the default Featured sort.
                  </span>
                </div>
              </li>
              <li>
                <BenefitIcon>
                  <RefreshIcon />
                </BenefitIcon>
                <div>
                  <strong>24-hour crawling</strong>
                  <span>
                    Prices, stock, and new products are refreshed within 24
                    hours - far faster than standard listings.
                  </span>
                </div>
              </li>
            </ul>
          </div>

          <div className="premium-page__features" aria-label="Premium features">
            <article className="premium-page__feature">
              <div className="premium-page__feature-head">
                <span className="premium-page__feature-number">1</span>
                <h2 className="premium-page__feature-title">
                  Homepage and Category Features
                </h2>
              </div>
              <p className="premium-page__feature-copy">
                Your shop is eligible for getting featured on the homepage
                daily, plus 1 main category and 3 subcategories of your choice (
                <span className="premium-page__category-preview premium-page__category-preview--inline">
                  <CategoryMenuDropdown
                    browseCategories={browseCategories}
                    activeShopCategorySlug=""
                    readOnly
                    modal
                    triggerLabel="view available categories"
                    triggerVariant="link"
                  />
                </span>
                ), where your logo, brand, and products appear front and
                center - putting your brand in front of shoppers with high
                intent to buy while they&apos;re browsing and open to
                discovering something new.
              </p>
              <div className="premium-page__placements-demo">
                <FeaturedPremiumShopSection
                  shop={DEMO_PREMIUM_SHOP}
                  products={DEMO_FEATURED_PRODUCTS}
                  total={48}
                  outboundCampaign="premium_page_demo"
                  analyticsSource="premium"
                />
              </div>
            </article>

            <article className="premium-page__feature">
              <div className="premium-page__feature-head">
                <span className="premium-page__feature-number">2</span>
                <h2 className="premium-page__feature-title">
                  Branded and More Engaging Product Cards
                </h2>
              </div>
              <p className="premium-page__feature-copy">
                Premium cards look and perform differently from standard
                ones.
              </p>
              <div className="premium-page__card-guide-wrap">
                <PremiumCardFeatureGuide />
              </div>
            </article>

            <article className="premium-page__feature">
              <div className="premium-page__feature-head">
                <span className="premium-page__feature-number">3</span>
                <h2 className="premium-page__feature-title">
                  Boosted Placement
                </h2>
              </div>
              <p className="premium-page__feature-copy">
                When your products show in search results, Premium boosts them
                closer to the top - especially under the default
                &quot;Featured&quot; sort, where most shoppers start comparing.
              </p>
              <ul className="premium-page__callouts" aria-label="Why boosted placement matters">
                <li className="premium-page__callout">
                  <BenefitIcon>
                    <SortIcon />
                  </BenefitIcon>
                  <div>
                    <strong>Search result boost</strong>
                    <span>
                      Every time your products appear in search results, Premium
                      lifts them in the default Featured ranking most shoppers
                      use.
                    </span>
                  </div>
                </li>
                <li className="premium-page__callout">
                  <BenefitIcon>
                    <RankIcon />
                  </BenefitIcon>
                  <div>
                    <strong>
                      <span className="premium-page__stat">26.5% CTR</span>{' '}
                      average on search &amp; browse
                    </strong>
                    <span>
                      That&apos;s the site-wide average - listings higher in the
                      results capture an even larger share of clicks.
                    </span>
                  </div>
                </li>
              </ul>
            </article>

            <article className="premium-page__feature">
              <div className="premium-page__feature-head">
                <span className="premium-page__feature-number">4</span>
                <h2 className="premium-page__feature-title">24-Hour Crawling</h2>
              </div>
              <p className="premium-page__feature-copy">
                Prices, stock, discounts, new products, and delistings are all
                refreshed within 24 hours. Standard listings follow a much
                slower crawl schedule - Premium keeps your catalog accurate
                while shoppers are still comparing, not days later.
              </p>
              <ul className="premium-page__callouts premium-page__callouts--pair" aria-label="Why 24-hour crawling matters">
                <li className="premium-page__callout">
                  <BenefitIcon>
                    <RefreshIcon />
                  </BenefitIcon>
                  <div>
                    <strong>Within 24 hours</strong>
                    <span>
                      Your full catalog is re-checked within 24 hours - a sharp
                      contrast to the slower refresh cycle standard shops wait
                      on.
                    </span>
                  </div>
                </li>
                <li className="premium-page__callout">
                  <BenefitIcon>
                    <ShieldIcon />
                  </BenefitIcon>
                  <div>
                    <strong>Shopper trust</strong>
                    <span>
                      Stale listings erode confidence at the click - and
                      disappointment after it. Fresher data removes that doubt.
                      Premium cards show ✓ today so shoppers know stock was
                      verified today, not days ago.
                    </span>
                  </div>
                </li>
              </ul>
            </article>
          </div>
        </div>

        <section className="premium-page__pricing" aria-labelledby="premium-cta-heading">
          <div className="premium-page__cta">
            <h2 id="premium-cta-heading" className="premium-page__cta-title">
              Pricing
            </h2>
            <p className="premium-page__cta-lead">
              Introductory offer - lock in your monthly rate for a full year,
              even if Shops.mt&apos;s traffic grows 10x.
            </p>
            <div className="premium-page__pricing-table-wrap">
              <table className="premium-page__pricing-table">
                <caption className="premium-page__pricing-caption">
                  Monthly Premium pricing by catalog size
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Catalog size</th>
                    <th scope="col">Monthly price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Up to 1,500 items</td>
                    <td>
                      <span className="premium-page__price-was" aria-label="Standard price €59">
                        €59
                      </span>{' '}
                      <span className="premium-page__price-now">€35</span>
                    </td>
                  </tr>
                  <tr>
                    <td>1,500–10,000 items</td>
                    <td>
                      <span className="premium-page__price-was" aria-label="Standard price €99">
                        €99
                      </span>{' '}
                      <span className="premium-page__price-now">€65</span>
                    </td>
                  </tr>
                  <tr>
                    <td>10,000–30,000 items</td>
                    <td>
                      <span className="premium-page__price-was" aria-label="Standard price €179">
                        €179
                      </span>{' '}
                      <span className="premium-page__price-now">€129</span>
                    </td>
                  </tr>
                  <tr>
                    <td>30,000+ items</td>
                    <td>
                      <span className="premium-page__price-custom">Custom pricing</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul className="premium-page__cta-bullets">
              <li>
                A fraction of what a single week of banner ads costs elsewhere -
                priced so every Maltese shop can be found by people already
                searching to buy.
              </li>
              <li>
                Your monthly price stays exactly where it started for 12
                months - even if Shops.mt&apos;s traffic grows significantly.
              </li>
            </ul>
          </div>

          <div className="premium-page__contact">
            <p className="premium-page__contact-lead">
              Interested in the Premium Package?
            </p>
            <p className="premium-page__contact-action">
              <span>Send an email to</span>
              <a
                className="premium-page__contact-cta"
                href="mailto:viktor.jansson@shops.mt?subject=Premium%20Package%20inquiry"
              >
                viktor.jansson@shops.mt
              </a>
            </p>
          </div>
        </section>
      </div>
      <SiteFooter />
    </div>
  );
}
